<script lang="ts">
  import Icon from './Icon.svelte'
  import SearchForm from './SearchForm.svelte'

  /**
   * The phone's search (Phase 11 plan §5.1): a trigger, and the field in a
   * panel that drops from the top edge. Two triggers open the same panel —
   * the square ink button in a section's panel bar (`ink`), and the search
   * field floating over the Map section (`field`), which is a button drawn as
   * a field so the keyboard comes up in the panel rather than over the map.
   *
   * The panel drops from the top rather than rising from the bottom because
   * the on-screen keyboard takes the bottom half of the phone the moment the
   * field is focused. Saved places are the Places section now, not a list
   * under the field.
   */

  interface Props {
    variant: 'ink' | 'field'
    onResolved?: (source: 'search' | 'geo') => void
  }

  let { variant, onResolved }: Props = $props()

  let open = $state(false)
  let field: HTMLElement | null = $state(null)

  // Opened by a deliberate tap, so put the caret where the tap was heading.
  $effect(() => {
    field?.querySelector('input')?.focus()
  })

  function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') open = false
  }

  function resolved(source: 'search' | 'geo') {
    open = false
    onResolved?.(source)
  }
</script>

<svelte:window onkeydown={open ? onKeydown : undefined} />

{#if variant === 'ink'}
  <button
    type="button"
    class="wx-ink-btn touch-target"
    aria-label="Search"
    title="Search"
    aria-expanded={open}
    onclick={() => (open = !open)}
  >
    <Icon name="search" size={20} />
  </button>
{:else}
  <button
    type="button"
    class="search-field touch-target"
    aria-label="Search a city or ZIP"
    aria-expanded={open}
    onclick={() => (open = !open)}
  >
    <Icon name="search" size={20} />
    <span class="placeholder" aria-hidden="true">Search a city or ZIP</span>
  </button>
{/if}

{#if open}
  <!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_static_element_interactions -->
  <div class="scrim" onclick={() => (open = false)}></div>
  <div class="search-panel" role="dialog" aria-label="Search" aria-modal="true">
    <div class="top">
      <div class="field" bind:this={field}>
        <SearchForm onResolved={resolved} />
      </div>
      <button type="button" class="close touch-target" onclick={() => (open = false)}>Cancel</button>
    </div>
  </div>
{/if}

<style>
  /* The Map section's field: 48 px of ground on the float shadow, square. */
  .search-field {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    height: 48px;
    padding: 0 14px;
    border: 0;
    background: var(--color-bg);
    box-shadow: var(--float-shadow);
    color: var(--color-text);
    font-size: 16px;
    text-align: left;
    cursor: text;
  }

  .placeholder {
    color: var(--color-muted);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  /* Whatever is behind stays visible, dimmed, between the panel and the
     keyboard. Fixed, because a trigger can sit in a panel bar or over the map. */
  .scrim {
    position: fixed;
    inset: 0;
    z-index: 40;
    background: rgb(0 0 0 / 35%);
  }

  /* Flush with the top edge, the notch inset inside it: the keyboard owns
     the bottom third the moment the field takes focus. */
  .search-panel {
    position: fixed;
    z-index: 41;
    top: 0;
    left: 0;
    right: 0;
    max-height: 100dvh;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding-top: calc(var(--safe-top) + 4px);
    padding-bottom: 12px;
    background: var(--color-bg);
    border-bottom: 2px solid var(--color-text);
    box-shadow: var(--shadow-lg);
  }

  .top {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 4px;
    align-items: start;
    padding: 4px 8px 0 16px;
  }

  .field {
    min-width: 0;
  }

  .close {
    height: 48px;
    padding: 0 8px;
    border: 0;
    background: none;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
  }
</style>
